import { useSeasonalEvent } from "@/contexts/SeasonalEventContext";
import { isChristmasSeason } from "@/config/christmasTheme";
import { isNewYearPeriod } from "@/config/seasonalEvents";
import { isValentinePeriod } from "@/config/valentines";
import { isHalloweenPeriod } from "@/config/halloween";
import { useIsStaff } from "@/hooks/useIsStaff";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  parseHolidayPreviewParam,
  readStoredHolidayPreview,
  writeStoredHolidayPreview,
  type HolidayPreview,
} from "@/lib/holidayPreview";

export type HolidayKind = "christmas" | "new_year" | "valentine" | "halloween" | null;

/**
 * Single holiday detector for site chrome.
 * Staff/owner can force a theme in this browser only via
 * ?holiday=christmas|newyear|valentine|halloween|off
 * (stored in sessionStorage — other visitors are unaffected).
 */
export function useHolidayTheme() {
  const seasonal = useSeasonalEvent();
  const { activeEvent } = seasonal;
  const { isStaff } = useIsStaff();
  const [searchParams, setSearchParams] = useSearchParams();
  const [preview, setPreview] = useState<HolidayPreview>(() => readStoredHolidayPreview());

  useEffect(() => {
    const parsed = parseHolidayPreviewParam(searchParams.get("holiday"));
    if (parsed === undefined) return;
    if (!isStaff) return;

    writeStoredHolidayPreview(parsed);
    setPreview(parsed);

    const next = new URLSearchParams(searchParams);
    next.delete("holiday");
    setSearchParams(next, { replace: true });
  }, [isStaff, searchParams, setSearchParams]);

  const clearPreview = () => {
    writeStoredHolidayPreview(null);
    setPreview(null);
  };

  const calendarChristmas = isChristmasSeason();
  const calendarNewYear = isNewYearPeriod();
  const calendarValentine = isValentinePeriod();
  const calendarHalloween = isHalloweenPeriod();

  const staffPreview = isStaff ? preview : null;

  let isChristmas =
    activeEvent === "CHRISTMAS" ||
    (calendarChristmas && activeEvent !== "NEW_YEAR" && activeEvent !== "VALENTINE" && activeEvent !== "HALLOWEEN");
  let isNewYear =
    activeEvent === "NEW_YEAR" ||
    (!isChristmas && calendarNewYear && activeEvent !== "VALENTINE" && activeEvent !== "HALLOWEEN");
  let isValentine =
    activeEvent === "VALENTINE" ||
    (!isChristmas && !isNewYear && calendarValentine && activeEvent !== "HALLOWEEN");
  let isHalloween =
    activeEvent === "HALLOWEEN" || (!isChristmas && !isNewYear && !isValentine && calendarHalloween);

  const applyPreview = (next: {
    christmas: boolean;
    newYear: boolean;
    valentine: boolean;
    halloween: boolean;
  }) => {
    isChristmas = next.christmas;
    isNewYear = next.newYear;
    isValentine = next.valentine;
    isHalloween = next.halloween;
  };

  if (staffPreview === "christmas") {
    applyPreview({ christmas: true, newYear: false, valentine: false, halloween: false });
  } else if (staffPreview === "new_year") {
    applyPreview({ christmas: false, newYear: true, valentine: false, halloween: false });
  } else if (staffPreview === "valentine") {
    applyPreview({ christmas: false, newYear: false, valentine: true, halloween: false });
  } else if (staffPreview === "halloween") {
    applyPreview({ christmas: false, newYear: false, valentine: false, halloween: true });
  } else if (staffPreview === "off") {
    applyPreview({ christmas: false, newYear: false, valentine: false, halloween: false });
  }

  const kind: HolidayKind = isHalloween
    ? "halloween"
    : isValentine
      ? "valentine"
      : isNewYear
        ? "new_year"
        : isChristmas
          ? "christmas"
          : null;

  return {
    ...seasonal,
    isChristmas,
    isNewYear,
    isValentine,
    isHalloween,
    kind,
    preview: staffPreview,
    isPreview:
      staffPreview === "christmas" ||
      staffPreview === "new_year" ||
      staffPreview === "valentine" ||
      staffPreview === "halloween" ||
      staffPreview === "off",
    clearPreview,
  };
}
